import React, { useCallback, useMemo } from 'react';
import {
  AppHeader,
  Avatar,
  decOfNum,
  IconButton,
  IconMindlyColored,
  IconRenew,
  IconShare,
  Skeleton,
  IconArrowLeft,
  IconFilters,
} from '@mindly/ui-components';
import { IonIcon, useIonRouter } from '@ionic/react';
import { useSelector } from 'react-redux';
import { userSelector } from '../../redux/user/selector';
import { lastActiveSubscriptionSelector } from '../../redux/subscriptions/selector';
import { FormattedMessage, useIntl } from 'react-intl';
import useLangs from '../../context/LangsContext';
import { informationCircleOutline } from 'ionicons/icons';
import { PageHeaderEndSlot, PageHeaderStartSlot, PageHeaderTitle } from './PageHeader.styled';
import { ImpactStyle } from '@capacitor/haptics';
import { useHistory } from 'react-router-dom';
import { PrivateRoutes } from '../../routes/navigation';
import classNames from 'classnames';
import { pageAnimation, useAnalytics } from 'src/shared';
import { PromoCodeTimerFeature } from '../../features/PromoCodeTimerFeature';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';
import { specialistsFiltersIsSetSelector } from 'src/redux/specialists/selector';
import { hasUnUsedPromoCodeSelector } from 'src/redux/promoCodes/promoCodes.selectors';
import useIntercomPlugin from 'src/shared/hooks/useIntercomPlugin';

type PageHeaderProps = {
  intercomId?: number;
  showBalance?: boolean;
  backChevron?: boolean;
  backChevronAction?: () => void;
  showAvatar?: boolean;
  title?: string;
  subtitle?: string | boolean;
  children?: JSX.Element | null;
  loading?: boolean;
  childrenInCenterSlot?: boolean;
  onShare?: () => void;
  showLogo?: boolean;
  showFilters?: boolean;
  showChatButton?: boolean;
  onOnboardingRestart?(): void;
  className?: string;
  showPromoCodeTimer?: boolean;
  hideWhenEmpty?: boolean;
  toolbarClassName?: string;
};

const PageHeader: React.FC<PageHeaderProps> = ({
  intercomId,
  showBalance,
  backChevron,
  backChevronAction,
  showAvatar = true,
  title,
  subtitle,
  children,
  loading,
  childrenInCenterSlot = false,
  onShare,
  showLogo,
  onOnboardingRestart,
  showFilters,
  className,
  showPromoCodeTimer = false,
  toolbarClassName,
}) => {
  const intl = useIntl();
  const { locale } = useLangs();
  const { displayArticle } = useIntercomPlugin();
  const history = useHistory();
  const navigation = useIonRouter();

  const currentUser = useSelector(userSelector);
  const lastSubscription = useSelector(lastActiveSubscriptionSelector());
  const isFiltersSet = useSelector(specialistsFiltersIsSetSelector);
  const { trackPredefinedEvent } = useAnalytics();
  const hasUnUsedPromoCode = useSelector(hasUnUsedPromoCodeSelector);

  const subscriptionBalance = useMemo(() => {
    if (
      !Number.isInteger(lastSubscription?.session_count) ||
      !Number.isInteger(lastSubscription?.used_balance) ||
      !lastSubscription
    )
      return null;

    return lastSubscription.session_count - lastSubscription.used_balance;
  }, [lastSubscription?.session_count, lastSubscription?.used_balance, lastSubscription]);

  const handleClickBackButton = useCallback(() => {
    backChevronAction
      ? backChevronAction()
      : navigation.canGoBack()
        ? navigation.goBack((baseEl, opts) => pageAnimation(baseEl, opts, true))
        : navigation.push(PrivateRoutes.PSYCHOLOGIST_LIST, 'none');
  }, [backChevronAction, navigation]);

  const handleClickFiltersButton = useCallback(async () => {
    isomorficHapticsImpact({ style: ImpactStyle.Light });
    trackPredefinedEvent('SPECIALIST_LIST_FILTER_BUTTON_CLICKED');
    history.push(PrivateRoutes.PSYCHOLOGIST_LIST_FILTERS);
  }, [history, trackPredefinedEvent]);

  const timerBody = useMemo(() => {
    if (showPromoCodeTimer && hasUnUsedPromoCode) {
      return <PromoCodeTimerFeature expandWidth />;
    }
    return null;
  }, [showPromoCodeTimer, hasUnUsedPromoCode]);

  const additionalHeaderClassName = useMemo(() => {
    if (showPromoCodeTimer && hasUnUsedPromoCode) {
      // 61px - height of the promocode banner
      return 'before:content-[""] before:absolute before:bottom-0 before:left-0 before:w-full before:h-[calc(100%-61px)] before:bg-color-background-white !bg-badges-info-bg';
    }
    return '';
  }, [showPromoCodeTimer, hasUnUsedPromoCode]);

  const additionalToolbarClassName = useMemo(() => {
    return classNames(toolbarClassName, {
      '![--background:transparent]': showPromoCodeTimer && hasUnUsedPromoCode,
    });
  }, [showPromoCodeTimer, hasUnUsedPromoCode, toolbarClassName]);

  if (loading)
    return (
      <AppHeader
        className={classNames(className, additionalHeaderClassName)}
        toolbarClassName={classNames(additionalToolbarClassName)}
      >
        <div className="flex flex-col gap-3">
          {timerBody}
          <PageHeaderTitle className="flex items-center justify-between !flex-row">
            <Skeleton widthPx={32} heightPx={32} />
            <div>
              <Skeleton widthPx={92} heightPx={16}></Skeleton>
              <Skeleton widthPx={58} heightPx={14}></Skeleton>
            </div>
            <Skeleton widthPx={32} heightPx={32} />
          </PageHeaderTitle>
        </div>
      </AppHeader>
    );

  return (
    <AppHeader
      className={classNames(className, additionalHeaderClassName)}
      toolbarClassName={classNames(additionalToolbarClassName)}
    >
      <div className="flex flex-col gap-3">
        {timerBody}

        <div className={classNames('flex items-center justify-between')}>
          {backChevron || showAvatar || showLogo ? (
            <PageHeaderStartSlot slot={'start'}>
              {backChevron ? (
                <IconButton icon={<IconArrowLeft />} onClick={handleClickBackButton} className="p-2" />
              ) : showAvatar ? (
                <Avatar
                  dimensions={35}
                  src={currentUser?.avatar}
                  className="cursor-pointer"
                  onClick={async () => {
                    isomorficHapticsImpact({ style: ImpactStyle.Light });
                    history.push(PrivateRoutes.PROFILE_SETTINGS_TAB);
                  }}
                />
              ) : showLogo ? (
                <IconMindlyColored />
              ) : null}
            </PageHeaderStartSlot>
          ) : null}
          {(title || subtitle) && (
            <PageHeaderTitle>
              {title && <div className={`header-title ${!subtitle ? 'big-title' : ''}`}>{title}</div>}
              {subtitle && <div className="header-subtitle">{subtitle}</div>}
            </PageHeaderTitle>
          )}
          {childrenInCenterSlot && children}
          <PageHeaderEndSlot slot={'end'} onClick={() => null} className="!gap-0">
            {showFilters && (
              <div className="relative">
                <IconButton icon={<IconFilters fill="none" />} onClick={handleClickFiltersButton} className="p-2" />
                {isFiltersSet && (
                  <span className={'rounded-full bg-[#c13e18] w-[7px] h-[7px] absolute top-2 right-[3px]'} />
                )}
              </div>
            )}
            {onOnboardingRestart && <IconButton icon={<IconRenew />} className="p-2" onClick={onOnboardingRestart} />}
            {onShare && <IconButton className="p-2 cursor-pointer" icon={<IconShare />} onClick={onShare} />}
            {!childrenInCenterSlot && children}
            {showBalance && lastSubscription && Number.isInteger(subscriptionBalance) && (
              <div className={'balance-wrapper'}>
                <div>
                  <FormattedMessage id="subscriptions.balance" defaultMessage="Баланс" />:{' '}
                  <b>
                    {subscriptionBalance}{' '}
                    {decOfNum(subscriptionBalance ?? 0, [
                      intl.formatMessage({
                        id: 'other.sessions.one',
                        defaultMessage: 'сеанс',
                      }),
                      intl.formatMessage({
                        id: 'other.sessions.many',
                        defaultMessage: 'сеанси',
                      }),
                      intl.formatMessage({
                        id: 'other.sessions.several',
                        defaultMessage: 'сеансів',
                      }),
                    ])}
                  </b>
                </div>
                <div className={'subscription-period'}>
                  <FormattedMessage id="subscriptions.valid" defaultMessage="Дійсні до" />:{' '}
                  <b>
                    {new Date(lastSubscription.period_end * 1000).toLocaleString(locale, {
                      day: 'numeric',
                      month: 'long',
                    })}
                  </b>
                </div>
              </div>
            )}
            {intercomId && (
              <button
                className={'information-icon-wrapper'}
                onClick={async () => {
                  await displayArticle(intercomId);
                }}
              >
                <IonIcon icon={informationCircleOutline} />
              </button>
            )}
          </PageHeaderEndSlot>
        </div>
      </div>
    </AppHeader>
  );
};

export default PageHeader;
